import {
    DateCalendar,
    DayCalendarSkeleton,
    LocalizationProvider,
    PickersDay,
    PickersDayProps,
} from '@mui/x-date-pickers-pro';
import type { PickerValue } from '@mui/x-date-pickers/internals';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { tzdate, TZDate } from '@repo/tzdate';
import { useLocale } from '@repo/i18n';
import dayjs, { Dayjs } from 'dayjs';
import { useTheme } from '@mui/material/styles';
import { Box } from '@mui/material';
import { getCalendarDayStyles } from 'src/components/utils/jss/CalendarStyleUtils';
import { getWeekNumbersForMonth } from 'src/components/utils/dateUtils';
import WeekNumbersColumn from './subcomponents/WeekNumbersColumn';
import { useState, useEffect, useMemo, type JSX, type ReactNode } from 'react';

interface IProps {
    date: TZDate | null;
    onChange: (newDate: TZDate | null) => void;
    onMonthChange?: (date: TZDate | null) => void;
    onYearChange?: (date: TZDate | null) => void;
    minDate?: TZDate;
    maxDate?: TZDate;
    shouldDisableDate?: (date: string | null) => boolean;
    loading?: boolean;
    loadingOverlay?: ReactNode;
    highlightedDates?: Map<string, string>;
    showWeekNumbers?: boolean;
    weekNumberRowHeight?: number;
    weekNumberTopPadding?: number;
}

export default function BilberryStaticCalendar(props: IProps): JSX.Element {
    const {
        date,
        onChange,
        onMonthChange,
        onYearChange,
        minDate,
        maxDate,
        shouldDisableDate: shouldDisableDateProp,
        loading,
        loadingOverlay,
        highlightedDates,
        showWeekNumbers = false,
        weekNumberRowHeight = 36,
        weekNumberTopPadding = 96,
    } = props;

    const { dayjsLocale } = useLocale();
    const theme = useTheme();
    const [currentMonth, setCurrentMonth] = useState<Dayjs>(
        date?.forceLocalTz().getDayjsDate() ?? tzdate().getDayjsDate(),
    );

    useEffect(() => {
        if (date) {
            setCurrentMonth(date.forceLocalTz().getDayjsDate());
        }
    }, [date]);

    const weekNumbers = useMemo(() => {
        return showWeekNumbers ? getWeekNumbersForMonth(currentMonth) : [];
    }, [showWeekNumbers, currentMonth]);

    const textColor = useMemo(() => theme.palette.text.secondary, [theme.palette.text.secondary]);

    const calendarPicker = (
        <DateCalendar
            autoFocus={true}
            value={date?.forceLocalTz().getDayjsDate() ?? null}
            views={['day']}
            onChange={(value: PickerValue) => {
                const d = value as Dayjs | null;
                const date = d ? tzdate(d) : d;
                onChange(date);
            }}
            onMonthChange={(month: Dayjs | TZDate) => {
                const d = month instanceof TZDate ? month.getDayjsDate() : month;
                setCurrentMonth(d);
                // Parse the new month's first day directly in the site timezone via
                // dayjs.tz(). Chained TZDate mutators (.month(), .date(), …) inherit
                // the source dayjs's cached UTC offset and don't recompute it when
                // crossing DST boundaries, so a TZDate constructed from "now" in May
                // (Oslo CEST +02) then advanced to November ends up as "Nov 1 00:00
                // +02:00" — an instant that doesn't exist for Oslo (Nov is CET +01).
                // startOf('month') then collapses that to October, the cache key
                // matches the October fetch, and the November request never fires.
                // dayjs.tz(str, zone) parses fresh and resolves the right offset for
                // the target date. See SB-869.
                const date = tzdate(dayjs.tz(d.format('YYYY-MM-01'), TZDate.timezone), false);
                onMonthChange?.(date);
            }}
            onYearChange={(year: Dayjs | TZDate) => {
                const d = year instanceof TZDate ? year.getDayjsDate() : year;
                // Same DST-offset hazard as onMonthChange — see SB-869.
                const date = tzdate(dayjs.tz(d.format('YYYY-MM-01'), TZDate.timezone), false);
                onYearChange?.(date);
            }}
            minDate={minDate?.forceLocalTz().getDayjsDate()}
            maxDate={maxDate?.forceLocalTz().getDayjsDate()}
            slots={{
                day: (dayProps: PickersDayProps) => {
                    const _day = dayProps.day as Dayjs;
                    const formattedDate = _day.format('YYYY-MM-DD');
                    const statusFromDate = highlightedDates?.get(formattedDate);
                    const highlightStyle = statusFromDate ? { status: statusFromDate } : undefined;
                    const isDisabled = shouldDisableDateProp?.(formattedDate) ?? false;
                    const dayStyles = getCalendarDayStyles(highlightStyle, theme);

                    const selected = tzdate(_day).isSame(date, 'day');

                    const firstDayOfMonthOffsetName = _day.clone().startOf('month').offsetName();
                    const renderDateOffsetName = _day.offsetName();
                    const selectedDateHasDifferentOffsetName =
                        firstDayOfMonthOffsetName !== renderDateOffsetName;

                    return (
                        <PickersDay
                            {...dayProps}
                            tabIndex={0}
                            autoFocus={dayProps.autoFocus && !selectedDateHasDifferentOffsetName}
                            selected={selected}
                            disabled={isDisabled}
                            sx={dayStyles}
                        />
                    );
                },
            }}
            disableHighlightToday={true}
            shouldDisableDate={(day: Dayjs | TZDate) => {
                if (!shouldDisableDateProp) return false;
                const d = day instanceof TZDate ? day.getDayjsDate() : day;
                const res = shouldDisableDateProp?.(d.format('YYYY-MM-DD'));
                return res ?? false;
            }}
            loading={loading}
            renderLoading={() => (
                <Box sx={{ position: 'relative' }}>
                    <DayCalendarSkeleton />
                    {loadingOverlay}
                </Box>
            )}
        />
    );

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={dayjsLocale}>
            {showWeekNumbers ? (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
                    <WeekNumbersColumn
                        weekNumbers={weekNumbers}
                        textColor={textColor}
                        rowHeight={weekNumberRowHeight}
                        topPadding={weekNumberTopPadding}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>{calendarPicker}</Box>
                </Box>
            ) : (
                calendarPicker
            )}
        </LocalizationProvider>
    );
}
